import React, { useState, FC, MouseEvent, ChangeEvent } from 'react';
import './input.css';
import { validateInput } from '../../utils/validators/Validators';
import { Box, Chip, TextField, IconButton, TextFieldProps, Typography } from '@mui/material';
import { ArrowDropDown, ArrowDropUp, InfoOutlined, Visibility, VisibilityOff, WarningAmberOutlined } from '@mui/icons-material';

import type { FormValidators, GuestVarianceTone } from '../Form';
import { ToolTip } from 'components/Tooltip/ToolTip';
import strings from 'common/Translation/Translate';

interface OwnProps {
  leftIcon?: string;
  inputValue?: string;
  field?: string;
  typeValue?: string;
  textChange?: (value: string, field: string, error?: { error: boolean; message: string }) => void;
  validators?: FormValidators[];
  hasError?: boolean;
  fieldError?: boolean | string;
  multiline?: boolean;
  rows?: number | string;
  minRows?: number | string;
  maxRows?: number | string;
  infoText?: any;
  belowText?: string;
  /** Shown on the same helper row as belowText, aligned to the right (e.g. guest variance). */
  belowTextRight?: string;
  belowTextRightTone?: GuestVarianceTone;
  /** Shown on the same row, to the right of the field (e.g. Hold Days release hint). */
  besideText?: string;
  /** When true, besideText renders with warning icon + warning color. */
  besideTextWarning?: boolean;
  requiredMessage?: string;
}

const unsignedVarianceLabel = (label: string) => String(label).replace(/^[+\-−–]/, '');

export const varianceChipSx = (tone: GuestVarianceTone = 'neutral') => ({
  height: 18,
  fontSize: 10,
  fontWeight: 600,
  flexShrink: 0,
  borderRadius: '9px',
  '& .MuiChip-label': { px: 0.75, py: 0 },
  '& .MuiChip-icon': { fontSize: 16, ml: 0.15, mr: -0.5, color: 'inherit' },
  ...(tone === 'positive'
    ? { bgcolor: '#E6F4EA', color: '#137333' }
    : tone === 'negative'
    ? { bgcolor: '#FCE8E6', color: '#C5221F' }
    : { bgcolor: 'action.hover', color: 'text.secondary' }),
});

export const VarianceChip: FC<{ label: string; tone?: GuestVarianceTone }> = ({ label, tone = 'neutral' }) => {
  const icon =
    tone === 'positive' ? <ArrowDropUp /> : tone === 'negative' ? <ArrowDropDown /> : undefined;
  return <Chip size="small" icon={icon} label={unsignedVarianceLabel(label)} sx={varianceChipSx(tone)} />;
};

export const VarianceQtyHint: FC<{ delta: number }> = ({ delta }) => {
  const ArrowIcon = delta > 0 ? ArrowDropUp : delta < 0 ? ArrowDropDown : null;
  return (
    <Typography
      component="span"
      sx={{ fontSize: 10, lineHeight: 1.2, color: 'text.secondary', display: 'inline-flex', alignItems: 'center' }}>
      {ArrowIcon ? <ArrowIcon sx={{ fontSize: 14, ml: -0.25 }} /> : null}
      {String(strings.formatString(strings.actualization_addon_qty_variance, Math.abs(delta)))}
    </Typography>
  );
};

export const FieldBelowHint: FC<{
  belowText?: string;
  belowTextRight?: string;
  belowTextRightTone?: GuestVarianceTone;
}> = ({ belowText, belowTextRight, belowTextRightTone = 'neutral' }) => {
  if (!belowText && !belowTextRight) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        mt: 0.35,
        width: '100%',
      }}>
      {belowText ? (
        <Typography fontWeight={500} fontSize="0.75rem" color="text.secondary" sx={{ lineHeight: 1.3 }}>
          {belowText}
        </Typography>
      ) : (
        <span />
      )}
      {belowTextRight ? <VarianceChip label={belowTextRight} tone={belowTextRightTone} /> : null}
    </Box>
  );
};

const InputField: FC<OwnProps & TextFieldProps> = ({
  hasError,
  validators,
  textChange,
  field,
  inputValue,
  placeholder,
  fieldError,
  inputProps,
  InputProps,
  variant,
  disabled,
  label,
  size = 'small',
  typeValue,
  style,
  autoFocus,
  sx,
  className,
  multiline = false,
  rows = 0,
  minRows = 1,
  maxRows = 1,
  infoText = null,
  belowText = undefined,
  belowTextRight = undefined,
  belowTextRightTone = 'neutral',
  besideText = undefined,
  besideTextWarning = false,
  requiredMessage,
  onFocus,
  ...rest
}) => {
  const [error, setError] = useState<{
    error: boolean;
    message: string;
  } | null>(null);
  const [pass, setPass] = useState(false);

  const handleChange = (value: string) => {
    if (typeValue === 'number') {
      if (!Number.isNaN(Number(value))) {
        const inputError = validateInput(validators, value);
        setError(inputError);
        if (textChange) {
          textChange(value, field as string, inputError);
        }
      }
    } else {
      const inputError = validateInput(validators, value);
      setError(inputError);
      if (textChange) {
        textChange(value, field as string, inputError);
      }
    }
  };
  const handleTogglePassword = () => {
    setPass(!pass);
  };

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };
  const customMessage = () => {
    if (!error && hasError && fieldError) {
      return requiredMessage || strings.requiredText;
    } else return '';
  };

  const endIcon = () => {
    let returnValue;
    if (typeValue === 'password') {
      returnValue = (
        <IconButton
          aria-label="toggle password visibility"
          onClick={handleTogglePassword}
          onMouseDown={handleMouseDownPassword}
          edge="end">
          {pass ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      );
    }
    if (infoText) {
      returnValue = (
        <ToolTip title={infoText}>
          <IconButton aria-label="infoText" color="primary" onClick={() => {}} edge="end">
            <InfoOutlined />
          </IconButton>
        </ToolTip>
      );
    }
    return returnValue;
  };
  const getType = () => {
    if (pass) {
      return 'text';
    } else {
      if (typeValue === 'number') return 'text';
      else return typeValue;
    }
  };
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        width: '100%',
      }}>
      <Box sx={{ flex: 1, minWidth: 0, ...sx }}>
        <TextField
          data-testid={'inputField'}
          autoFocus={autoFocus}
          sx={{
            width: '100%',
            'input[type=number]::-webkit-inner-spin-button, input[type=number]::-webkit-outer-spin-button': {
              WebkitAppearance: 'none',
            },
          }}
          size={size}
          margin="none"
          autoComplete={(inputProps as { autoComplete?: string } | undefined)?.autoComplete ?? 'off'}
          InputProps={{
            ...InputProps,
            startAdornment: InputProps?.startAdornment,
            endAdornment: endIcon() ?? InputProps?.endAdornment,
          }}
          inputProps={inputProps}
          style={style}
          disabled={disabled}
          type={getType()}
          color="primary"
          fullWidth
          className={`input-field ${fieldError}`}
          value={inputValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) => (textChange ? handleChange(e.target.value) : e)}
          onFocus={typeof onFocus === 'function' ? onFocus : undefined}
          placeholder={placeholder}
          label={label}
          InputLabelProps={{
            title: typeof label === 'string' ? label : undefined,
          }}
          variant={variant}
          multiline={multiline}
          rows={rows}
          minRows={minRows}
          maxRows={maxRows}
          {...rest}
          helperText={error && error.error ? error.message : customMessage()}
          error={(error && error.error) || (!error && hasError && fieldError) ? true : false}
        />
        <FieldBelowHint belowText={belowText} belowTextRight={belowTextRight} belowTextRightTone={belowTextRightTone} />
      </Box>
      {besideText ? (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.4,
            pt: 1.25,
            flexShrink: 0,
            maxWidth: { xs: '55%', sm: 260 },
            minWidth: 0,
          }}>
          {besideTextWarning ? (
            <WarningAmberOutlined sx={{ fontSize: 16, color: 'warning.main', flexShrink: 0 }} />
          ) : null}
          <Typography
            fontWeight={600}
            fontSize="0.75rem"
            sx={{
              color: besideTextWarning ? 'warning.dark' : 'text.secondary',
              lineHeight: 1.3,
              whiteSpace: 'normal',
            }}>
            {besideText}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};
export default InputField;
